import {
  ButtonGroup,
  Card,
  closeCurrentDialog,
  CustomRequestOption,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  isFile,
  Upload,
  UploadState,
} from 'platform/components';
import {Box, Show, VStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';
import {z} from 'zod';

import {useState} from 'react';

import {always, isNil, isNotNil} from 'ramda';

import {useUploadFileMutation} from '@dms/api/base';
import {DefinitionWithTemplatesResponseBody, useAddTemplateMutation} from '@dms/api/core';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {DOCX_MIME_TYPE, HTML_MIME_TYPE, useBoolean} from 'shared/web';

interface CreateTemplateFormProps {
  authorizationProfileId: string | null;
  documentCode: string;
  documentType: DefinitionWithTemplatesResponseBody['type'];
}

type FormValues = {
  title: string;
  note?: string | null;
};

type File = {
  fileUri: string;
  fileName: string;
  fileId: string;
  isPrefilled: boolean;
};

export function CreateTemplateForm(props: CreateTemplateFormProps) {
  const [uploadFile, {isLoading}] = useUploadFileMutation();
  const [addTemplate] = useAddTemplateMutation();

  const [isFileError, setIsFileError, clearFileError] = useBoolean();
  const [file, setFile] = useState<File | null>(null);

  const handleSubmit: FormSubmitHandler<FormValues> = async (values) => {
    if (isNil(file)) {
      setIsFileError();
      return;
    }

    await addTemplate({
      addTemplateRequestBody: {
        authorizationProfileId: props.authorizationProfileId,
        documentCode: props.documentCode,
        fileId: file.fileId,
        template: null,
        title: values.title,
        note: values.note || null,
      },
    })
      .unwrap()
      .then(() => {
        closeCurrentDialog();
      })
      .catch(handleApiError);
  };

  const handleUploadFile = async ({file}: CustomRequestOption) => {
    clearFileError();

    if (!isFile(file)) {
      return;
    }
    await uploadFile({file})
      .unwrap()
      .then((data) => {
        setFile({
          fileName: data.file.name,
          fileUri: data.fileUri,
          fileId: data.fileId,
          isPrefilled: false,
        });
      })
      .catch(handleApiError);
  };

  return (
    <Form<FormValues>
      onSubmit={handleSubmit}
      onInvalidSubmit={() => {
        if (isNil(file)) {
          setIsFileError();
        } else {
          clearFileError();
        }
      }}
      experimentalZodSchema={schema}
    >
      {(control, formApi) => {
        const title = formApi.watch('title');
        return (
          <VStack spacing={4}>
            <FormField
              control={control}
              name="title"
              label={i18n.t('entity.document.labels.templateTitle')}
              type="text"
              isRequired
            />

            <FormField
              control={control}
              name="note"
              label={i18n.t('entity.document.labels.note')}
              type="text"
            />

            <Show when={isNil(file)}>
              <Box height={30}>
                <Upload
                  type="card"
                  accept={props.documentType === 'docx' ? DOCX_MIME_TYPE : HTML_MIME_TYPE}
                  uploadState={match([isFileError, isLoading])
                    .with([true, Pattern.boolean], always(UploadState.Error))
                    .with([false, true], always(UploadState.Uploading))
                    .otherwise(always(UploadState.Idle))}
                  errorIcon="navigation/cancel"
                  uploadingIcon="action/hourglass_empty"
                  uploadIcon="file/upload"
                  customRequest={handleUploadFile}
                />
              </Box>
            </Show>

            <Show when={isNotNil(file)}>
              <VStack spacing={2}>
                <Card
                  title={`${title ?? props.documentCode}.${props.documentType}`}
                  parameters={[file?.fileName]}
                  colorStripe="blue"
                  actions={[
                    {
                      type: 'custom',
                      component: (
                        <Upload
                          type="button"
                          accept={props.documentType === 'docx' ? DOCX_MIME_TYPE : HTML_MIME_TYPE}
                          uploadText={i18n.t('entity.vehicle.labels.override')}
                          uploadState={isLoading ? UploadState.Uploading : UploadState.Idle}
                          errorIcon="navigation/cancel"
                          uploadingIcon="action/hourglass_empty"
                          uploadIcon="file/upload"
                          customRequest={handleUploadFile}
                        />
                      ),
                    },
                  ]}
                />
              </VStack>
            </Show>

            <ButtonGroup align="right">
              <FormButton
                variant="secondary"
                title={i18n.t('general.actions.discard')}
                control={control}
                onClick={closeCurrentDialog}
              />
              <FormButton
                type="submit"
                variant="primary"
                control={control}
                isDisabled={isLoading}
                title={i18n.t('general.actions.add')}
              />
            </ButtonGroup>
          </VStack>
        );
      }}
    </Form>
  );
}

const schema = z.object({
  title: z.string().nonempty(),
  note: z.string().nullable().optional(),
});
